import { db } from '@/api/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useMainContext } from '@/context/main-context';
import { doc, updateDoc } from 'firebase/firestore';
import * as React from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import PhoneNumberInput from '../ui/phone-number-input';
import { Switch } from '../ui/switch';
import { useToast } from '../ui/use-toast';

const TeacherEdit = ({ id, setCloseDialog }) => {
  const { teachers } = useMainContext();
  const teacher = teachers.find((s) => s.id === id);

  const { toast } = useToast();

  const defaultValues = {
    fullName: teacher?.fullName || '',
    position: teacher?.position || '',
    phone: teacher?.phone || '',
    address: teacher?.address || '',
    isActive: teacher?.isActive || false,
    salaryType: teacher?.salaryType || 'fixed',
    salaryAmount: teacher?.salaryAmount || 0,
    percentRate: teacher?.percentRate || 0,
  };

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    resetField,
    setValue,
  } = useForm({
    defaultValues,
  });

  const salaryType = useWatch({
    control,
    name: 'salaryType',
    defaultValue: defaultValues.salaryType,
  });

  React.useEffect(() => {
    if (salaryType === 'fixed') {
      resetField('percentRate');
      setValue('percentRate', 0);
    }
    if (salaryType === 'percent') {
      resetField('salaryAmount');
      setValue('salaryAmount', 0);
    }
    if (salaryType === 'hybrid') {
      // Ikkalasini ham saqlash kerak, hech narsa qilmaymiz
    }
  }, [salaryType, resetField, setValue]);

  const onSubmit = React.useCallback(
    async (data) => {
      try {
        const payload = {
          ...data,
          salaryType: data.salaryType,
          salaryAmount:
            data.salaryType === 'fixed' || data.salaryType === 'hybrid'
              ? data.salaryAmount || 0
              : 0,
          percentRate:
            data.salaryType === 'percent' || data.salaryType === 'hybrid'
              ? data.percentRate || 0
              : 0,
        };

        const docRef = doc(db, 'teachers', id);
        await updateDoc(docRef, payload);

        toast({ title: 'O‘qituvchi muvaffaqiyatli yangilandi' });
        setCloseDialog(false);
        reset();
      } catch (error) {
        toast({
          title: 'Xatolik yuz berdi',
          description: error.message,
          status: 'error',
        });
      }
    },
    [id, reset, setCloseDialog, toast]
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Ism va Lavozim */}
      <div className="flex flex-col md:flex-row items-center gap-2">
        <div className="w-full">
          <Label required htmlFor="fullName">
            Ism va Familiya
          </Label>
          <Input
            type="text"
            id="fullName"
            {...register('fullName', { required: 'Ism va familiya majburiy' })}
            placeholder="Ism Familiya kiriting"
          />
          {errors.fullName && (
            <p className="text-red-500">{errors.fullName.message}</p>
          )}
        </div>

        <div className="w-full">
          <Label required htmlFor="position">
            Lavozim
          </Label>
          <Input
            type="text"
            id="position"
            {...register('position', { required: 'Lavozim majburiy' })}
            placeholder="Lavozim kiriting"
          />
          {errors.position && (
            <p className="text-red-500">{errors.position.message}</p>
          )}
        </div>
      </div>

      {/* Telefon va Manzil */}
      <div className="flex flex-col md:flex-row items-center gap-2">
        <div className="w-full">
          <Label required htmlFor="phone">
            Telefon raqam
          </Label>
          <Controller
            name="phone"
            control={control}
            defaultValue=""
            rules={{
              required: 'Telefon raqam majburiy',
              minLength: { value: 10, message: 'Yetarlicha raqam kiritilmadi' },
            }}
            render={({ field }) => (
              <PhoneNumberInput
                id="phone"
                disabled={isSubmitting}
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          {errors.phone && (
            <p className="text-red-500">{errors.phone.message}</p>
          )}
        </div>

        <div className="w-full">
          <Label optional htmlFor="address">
            Manzil
          </Label>
          <Input
            type="text"
            id="address"
            {...register('address')}
            placeholder="Manzil kiriting"
          />
          {errors.address && (
            <p className="text-red-500">{errors.address.message}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 items-start gap-2">
        {/* SalaryType select */}
        <div className="w-full md:col-span-2">
          <Label required>Oylik turi</Label>
          <Controller
            name="salaryType"
            control={control}
            rules={{ required: "Bu yerni to'ldirish talab qilinadi" }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger>
                  <SelectValue placeholder="Oylik turini tanlang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed maosh</SelectItem>
                  <SelectItem value="percent">
                    Talabalardan to‘langan kurs narxidan foiz
                  </SelectItem>
                  <SelectItem value="hybrid">Kombinatsiya (hybrid)</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
          {errors.salaryType?.message && (
            <small className="text-xs text-red-500">
              {errors.salaryType?.message}
            </small>
          )}

          {salaryType === 'fixed' && (
            <small className="text-xs text-gray-500">
              O‘qituvchi belgilangan oylik maosh oladi.
            </small>
          )}
          {salaryType === 'percent' && (
            <small className="text-xs text-gray-500">
              O‘qituvchi talabalardan to‘langan kurs narxidan foiz asosida maosh
              oladi.
            </small>
          )}
          {salaryType === 'hybrid' && (
            <small className="text-xs text-gray-500">
              O‘qituvchi belgilangan oylik + foiz asosida maosh oladi.
            </small>
          )}
        </div>

        {salaryType === 'fixed' && (
          <div>
            <Label required>Oylik miqdori</Label>
            <Controller
              name="salaryAmount"
              control={control}
              rules={{ required: "Bu yerni to'ldirish talab qilinadi" }}
              render={({ field }) => (
                <Input
                  type="text"
                  placeholder="Masalan: 7 000 000 so'm"
                  value={
                    field.value
                      ? new Intl.NumberFormat('uz-UZ')
                          .format(field.value)
                          .replace(/\u202F/g, ' ')
                      : ''
                  }
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, ''); // faqat raqamlar
                    field.onChange(raw ? Number(raw) : '');
                  }}
                />
              )}
            />
          </div>
        )}

        {salaryType === 'percent' && (
          <div>
            <Label required>Foiz miqdori (%)</Label>
            <Input
              type="number"
              placeholder="Masalan: 30%"
              {...register('percentRate', {
                required: "Bu yerni to'ldirish talab qilinadi",
              })}
            />
          </div>
        )}

        {salaryType === 'hybrid' && (
          <>
            <div>
              <Label required>Fixed maosh</Label>
              <Controller
                name="salaryAmount"
                control={control}
                rules={{ required: "Bu yerni to'ldirish talab qilinadi" }}
                render={({ field }) => (
                  <Input
                    type="text"
                    placeholder="Masalan: 7 000 000 so'm"
                    value={
                      field.value
                        ? new Intl.NumberFormat('uz-UZ')
                            .format(field.value)
                            .replace(/\u202F/g, ' ')
                        : ''
                    }
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, ''); // faqat raqamlar
                      field.onChange(raw ? Number(raw) : '');
                    }}
                  />
                )}
              />
            </div>
            <div>
              <Label required>Foiz (%)</Label>
              <Input
                type="number"
                placeholder="Masalan: 20%"
                {...register('percentRate', {
                  required: "Bu yerni to'ldirish talab qilinadi",
                })}
              />
            </div>
          </>
        )}
      </div>

      {/* Faol holat */}
      <div className="flex items-center gap-2">
        <Controller
          name="isActive"
          control={control}
          render={({ field }) => (
            <Switch checked={field.value} onCheckedChange={field.onChange} />
          )}
        />
        <Label>Faolmi?</Label>
      </div>

      <Button className="float-right" type="submit" variant="default">
        {isSubmitting ? 'Saqlanmoqda...' : 'Saqlash'}
      </Button>
    </form>
  );
};

export default TeacherEdit;
